import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hrApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { ExpenseTypeSelect } from '@/components/selects';
import { Skeleton } from '@/components/ui/Skeleton';
import { HrLeaveReasonAttachmentFields } from '@/modules/hr/components/HrLeaveReasonAttachmentFields';
import { uploadHrDocument } from '@/modules/hr/components/leaveAttachmentUtils';
import { tierOptions } from '@/lib/query';
import type { HrExpenseClaim, HrExpenseType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface Props {
  claimId: number;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function ReturnedExpenseEditPanel({ claimId, onSaved, onCancel }: Props) {
  const { t } = useTranslation('hr');
  const [expenseTypeCode, setExpenseTypeCode] = useState('');
  const [claimDate, setClaimDate] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [description, setDescription] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expenseTypes, setExpenseTypes] = useState<HrExpenseType[]>([]);

  const { data: claimsPage, isLoading } = useQuery({
    queryKey: ['hr', 'expense-claim', claimId],
    queryFn: () => hrApi.listExpenses({ page: 0, size: 500 }),
    ...tierOptions('operational'),
  });

  const claim = useMemo(
    () => (claimsPage?.content ?? []).find((row: HrExpenseClaim) => row.id === claimId),
    [claimsPage, claimId],
  );

  useEffect(() => {
    hrApi
      .listExpenseTypes()
      .then(setExpenseTypes)
      .catch((err) => showApiError(err, t('errors.failedToLoadExpenseTypes')));
  }, [t]);

  useEffect(() => {
    if (!claim) return;
    setExpenseTypeCode(claim.expenseTypeCode ?? '');
    setClaimDate(claim.claimDate ?? '');
    setTotalAmount(String(claim.totalAmount ?? ''));
    setCurrencyCode(claim.currencyCode ?? 'USD');
    setDescription(claim.description ?? '');
    setExistingAttachmentUrl(claim.attachmentUrl ?? null);
    setAttachmentFile(null);
  }, [claim]);

  const selectedType = expenseTypes.find((type) => type.code === expenseTypeCode);
  const receiptRequired = selectedType?.requiresReceipt === true;

  const save = async () => {
    if (!claim || !expenseTypeCode || !claimDate || !totalAmount.trim() || !description.trim()) {
      notify.error(t('errors.employeeExpenseTypeDateAndAmountAreRequired'));
      return;
    }
    if (receiptRequired && !attachmentFile && !existingAttachmentUrl) {
      notify.error(t('errors.receiptRequiredForExpenseType'));
      return;
    }
    setSaving(true);
    setUploading(Boolean(attachmentFile));
    try {
      const uploadedUrl = attachmentFile ? await uploadHrDocument(attachmentFile) : undefined;
      const resolvedAttachmentUrl =
        uploadedUrl !== undefined
          ? uploadedUrl
          : existingAttachmentUrl === null
            ? ''
            : existingAttachmentUrl ?? undefined;
      await hrApi.updateExpense(claimId, {
        employeeId: claim.employeeId,
        claimDate,
        totalAmount: Number(totalAmount),
        currencyCode,
        expenseTypeCode,
        description: description.trim(),
        attachmentUrl: resolvedAttachmentUrl,
      });
      notify.t.success('toast.leaveUpdatedResubmitWhenReady', { ns: 'workflow' });
      onSaved?.();
    } catch (err) {
      showApiError(err, t('errors.failedToCreateExpense'));
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (!claim) {
    return <p className="text-sm text-slate-500">Expense claim not found.</p>;
  }

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Edit expense claim</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ExpenseTypeSelect value={expenseTypeCode} onChange={setExpenseTypeCode} required />
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Claim date</span>
          <input
            type="date"
            className="input w-full"
            value={claimDate}
            onChange={(e) => setClaimDate(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Amount</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            className="input w-full"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Currency</span>
          <input
            type="text"
            maxLength={10}
            className="input w-full uppercase"
            value={currencyCode}
            onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
          />
        </label>
      </div>
      <HrLeaveReasonAttachmentFields
        reason={description}
        onReasonChange={setDescription}
        attachmentFile={attachmentFile}
        onAttachmentFileChange={setAttachmentFile}
        existingAttachmentUrl={existingAttachmentUrl}
        onClearExistingAttachment={() => setExistingAttachmentUrl(null)}
        uploading={uploading}
        reasonLabel="Reason / description"
        attachmentLabel={receiptRequired ? 'Receipt (required)' : 'Receipt (optional)'}
        attachmentHint={
          receiptRequired
            ? 'This expense type requires a receipt or supporting document.'
            : 'Upload a receipt or invoice if available.'
        }
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          Save changes
        </Button>
        {onCancel ? (
          <Button size="sm" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );
}
